// admin/src/pages/ManageReviews.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import adminApi from '../services/adminApi';
import './ManageReviews.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://tripsie.in/api';
const SERVER_URL = API_BASE_URL.replace(/\/api\/?$/, '');
const getImageUrl = (p) => (!p ? '' : p.startsWith('http') ? p : `${SERVER_URL}${p}`);
const fmtDate = (d) => new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
const fmtSize = (b) => {
  if (b === null || b === undefined) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
};
const PAGE_SIZE = 10;
const STAR_PATH = 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z';

const STATUS_CONFIG = {
  all:      { label: 'All',      bg: '#f4f4f4', color: '#1a1e18', dot: '#1a1e18' },
  pending:  { label: 'Pending',  bg: '#fef3c7', color: '#92400e', dot: '#d97706' },
  approved: { label: 'Approved', bg: '#dcfce7', color: '#14532d', dot: '#16a34a' },
  rejected: { label: 'Rejected', bg: '#fee2e2', color: '#7f1d1d', dot: '#dc2626' },
};

const Stars = ({ rating }) => (
  <span className="rv-stars">
    {[1, 2, 3, 4, 5].map((n) => (
      <svg key={n} width="14" height="14" viewBox="0 0 24 24"
        fill={n <= rating ? '#facc15' : 'none'}
        stroke={n <= rating ? '#eab308' : '#d1d5db'} strokeWidth="1.5" strokeLinejoin="round">
        <path d={STAR_PATH} />
      </svg>
    ))}
  </span>
);

const Badge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <span className="rv-badge" style={{ background: cfg.bg, color: cfg.color }}>
      <span className="rv-badge__dot" style={{ background: cfg.dot }} />
      {cfg.label}
    </span>
  );
};

export default function ManageReviews() {
  const [allReviews, setAllReviews] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filter, setFilter]         = useState('pending');
  const [search, setSearch]         = useState('');
  const [page, setPage]             = useState(1);
  const [selected, setSelected]     = useState(null);
  const [busy, setBusy]             = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);
  const [error, setError]           = useState('');

  const fetchReviews = useCallback(async () => {
    try {
      setLoading(true); setError('');
      const res = await adminApi.getReviews('all');
      if (res.success) setAllReviews(res.reviews || []);
      else setError(res.message || 'Failed to load reviews.');
    } catch {
      setError('Cannot connect to server.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);
  useEffect(() => { setPage(1); }, [filter, search]);

  const counts = useMemo(() => {
    const c = { all: allReviews.length, pending: 0, approved: 0, rejected: 0 };
    allReviews.forEach((r) => { if (c[r.status] !== undefined) c[r.status] += 1; });
    return c;
  }, [allReviews]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allReviews.filter((r) => {
      if (filter !== 'all' && r.status !== filter) return false;
      if (!q) return true;
      return (
        (r.tripTitle || '').toLowerCase().includes(q) ||
        (r.userName || '').toLowerCase().includes(q) ||
        (r.text || '').toLowerCase().includes(q)
      );
    });
  }, [allReviews, filter, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const startIdx = (currentPage - 1) * PAGE_SIZE;
  const pageRows = filtered.slice(startIdx, startIdx + PAGE_SIZE);

  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const openModal = (r) => { setSelected(r); setShowReject(false); setRejectReason(''); setDeleteConfirm(false); };
  const closeModal = () => setSelected(null);

  const handleDelete = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_BASE_URL}/reviews/admin/${selected._id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setAllReviews((prev) => prev.filter((r) => r._id !== selected._id));
        closeModal();
      } else alert(data.message || 'Failed to delete.');
    } catch { alert('Failed. Please try again.'); }
    finally { setBusy(false); setDeleteConfirm(false); }
  };

  const handleApprove = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const res = await adminApi.approveReview(selected._id);
      if (res.success) {
        setAllReviews((prev) => prev.map((r) => (r._id === selected._id ? { ...r, status: 'approved', rejectReason: '' } : r)));
        closeModal();
      } else alert(res.message || 'Failed to approve.');
    } catch { alert('Failed. Please try again.'); }
    finally { setBusy(false); }
  };

  const handleReject = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const res = await adminApi.rejectReview(selected._id, rejectReason);
      if (res.success) {
        setAllReviews((prev) => prev.map((r) => (r._id === selected._id ? { ...r, status: 'rejected', rejectReason } : r)));
        closeModal();
      } else alert(res.message || 'Failed to reject.');
    } catch { alert('Failed. Please try again.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="rv">
      <div className="rv__header">
        <div>
          <h1 className="rv__title">Manage Reviews</h1>
          <p className="rv__sub">Approve or reject trip reviews submitted by travellers</p>
        </div>
        <button className="rv__refresh" onClick={fetchReviews} disabled={loading}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
          </svg>
          Refresh
        </button>
      </div>

      {error && <div className="rv__error">{error} <button onClick={fetchReviews}>Retry</button></div>}

      <div className="rv__card">
        {/* Toolbar */}
        <div className="rv__toolbar">
          <div className="rv__filters">
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
              <button key={key} className={`rv__filter-chip ${filter === key ? 'active' : ''}`} onClick={() => setFilter(key)}>
                {cfg.label}
                <span className="rv__filter-count">{counts[key]}</span>
              </button>
            ))}
          </div>
          <div className="rv__search">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input type="text" placeholder="Search trip, traveller or review" value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && <button className="rv__search-clear" onClick={() => setSearch('')} aria-label="Clear">×</button>}
          </div>
        </div>

        {/* Table */}
        <div className="rv__table-wrap">
          <table className="rv__table">
            <thead>
              <tr>
                <th>Traveller</th>
                <th>Trip</th>
                <th>Rating</th>
                <th className="rv__col-review">Review</th>
                <th className="rv__col-photos">Photos</th>
                <th className="rv__col-size">Size</th>
                <th className="rv__col-date">Date</th>
                <th>Status</th>
                <th className="rv__col-action" />
              </tr>
            </thead>
            <tbody>
              {loading && [...Array(6)].map((_, i) => (
                <tr key={`sk-${i}`} className="rv__row--skeleton">
                  <td><div className="rv__skel" style={{ width: 130 }} /></td>
                  <td><div className="rv__skel" style={{ width: 120 }} /></td>
                  <td><div className="rv__skel" style={{ width: 50 }} /></td>
                  <td className="rv__col-review"><div className="rv__skel" style={{ width: 200 }} /></td>
                  <td className="rv__col-photos"><div className="rv__skel" style={{ width: 60 }} /></td>
                  <td className="rv__col-size"><div className="rv__skel" style={{ width: 90 }} /></td>
                  <td className="rv__col-date"><div className="rv__skel" style={{ width: 80 }} /></td>
                  <td><div className="rv__skel" style={{ width: 78, height: 22, borderRadius: 999 }} /></td>
                  <td className="rv__col-action"><div className="rv__skel" style={{ width: 54, height: 30, borderRadius: 8 }} /></td>
                </tr>
              ))}

              {!loading && pageRows.map((r) => (
                <tr key={r._id} onClick={() => openModal(r)}>
                  <td>
                    <div className="rv__user">
                      <span className="rv__avatar">{(r.userName || 'T')[0].toUpperCase()}</span>
                      <span className="rv__user-name">{r.userName || 'Traveller'}</span>
                    </div>
                  </td>
                  <td><span className="rv__trip">{r.tripTitle || '—'}</span></td>
                  <td>
                    <span className="rv__rating">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="#facc15" stroke="#eab308" strokeWidth="1.5" strokeLinejoin="round"><path d={STAR_PATH} /></svg>
                      {r.rating}
                    </span>
                  </td>
                  <td className="rv__col-review">
                    <span className="rv__text">{r.text || <em>No written review</em>}</span>
                  </td>
                  <td className="rv__col-photos">
                    {r.photos?.length > 0 ? (
                      <div className="rv__thumbs">
                        {r.photos.slice(0, 2).map((p, i) => <img key={i} src={getImageUrl(p)} alt="" />)}
                      </div>
                    ) : <span className="rv__muted">—</span>}
                  </td>
                  <td className="rv__col-size">
                    {r.photoInfo?.length > 0 ? (
                      <div className="rv__sizes">
                        {r.photoInfo.map((p, i) => (
                          <span key={i} className="rv__size">
                            {p.originalSize != null && p.originalSize !== p.compressedSize && (
                              <><s>{fmtSize(p.originalSize)}</s> → </>
                            )}
                            <b>{fmtSize(p.compressedSize)}</b>
                          </span>
                        ))}
                      </div>
                    ) : <span className="rv__muted">—</span>}
                  </td>
                  <td className="rv__col-date"><span className="rv__muted">{fmtDate(r.createdAt)}</span></td>
                  <td><Badge status={r.status} /></td>
                  <td className="rv__col-action">
                    <button className="rv__view" onClick={(e) => { e.stopPropagation(); openModal(r); }}>
                      {r.status === 'pending' ? 'Review' : 'View'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {!loading && filtered.length === 0 && (
            <div className="rv__empty">
              <h3>No reviews found</h3>
              <p>
                {search
                  ? 'Nothing matches your search.'
                  : filter !== 'all' ? `No ${filter} reviews yet.` : 'Reviews will appear here once travellers submit them.'}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        {!loading && filtered.length > 0 && (
          <div className="rv__footer">
            <span className="rv__footer-info">
              Showing {startIdx + 1}–{Math.min(startIdx + PAGE_SIZE, filtered.length)} of {filtered.length}
            </span>
            <div className="rv__pager">
              <button onClick={() => setPage(currentPage - 1)} disabled={currentPage === 1}>Previous</button>
              <span>{currentPage} / {totalPages}</span>
              <button onClick={() => setPage(currentPage + 1)} disabled={currentPage === totalPages}>Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Detail modal */}
      {selected && (
        <div className="rv__overlay" onClick={closeModal}>
          <div className="rv__modal" onClick={(e) => e.stopPropagation()}>
            <div className="rv__modal-hd">
              <div><Badge status={selected.status} /><h2>{selected.tripTitle}</h2></div>
              <button className="rv__modal-close" onClick={closeModal} aria-label="Close">×</button>
            </div>
            <div className="rv__modal-body">
              <div className="rv__modal-top">
                <span className="rv__avatar rv__avatar--lg">{(selected.userName || 'T')[0].toUpperCase()}</span>
                <div>
                  <p className="rv__modal-name">{selected.userName || 'Traveller'}</p>
                  <p className="rv__modal-user">{fmtDate(selected.createdAt)}</p>
                </div>
                <div className="rv__modal-stars"><Stars rating={selected.rating} /></div>
              </div>

              {selected.text
                ? <p className="rv__modal-text">{selected.text}</p>
                : <p className="rv__modal-text rv__modal-text--empty">No written review.</p>}

              {selected.photos?.length > 0 && (
                <div className="rv__modal-photos">
                  {selected.photos.map((p, i) => (
                    <a key={i} href={getImageUrl(p)} target="_blank" rel="noopener noreferrer">
                      <img src={getImageUrl(p)} alt="" />
                    </a>
                  ))}
                </div>
              )}

              {selected.photoInfo?.length > 0 && (
                <div className="rv__modal-sizes">
                  {selected.photoInfo.map((p, i) => (
                    <span key={i}>
                      Photo {i + 1}: {p.originalSize != null && p.originalSize !== p.compressedSize ? `${fmtSize(p.originalSize)} → ` : ''}
                      <b>{fmtSize(p.compressedSize)}</b>
                    </span>
                  ))}
                </div>
              )}

              {selected.status === 'rejected' && selected.rejectReason && (
                <div className="rv__reject-note"><strong>Rejected:</strong> {selected.rejectReason}</div>
              )}

              {selected.status === 'pending' && !showReject && !deleteConfirm && (
                <div className="rv__modal-actions">
                  <button className="rv__act rv__act--approve" disabled={busy} onClick={handleApprove}>Approve</button>
                  <button className="rv__act rv__act--reject" disabled={busy} onClick={() => setShowReject(true)}>Reject</button>
                </div>
              )}
              {showReject && (
                <div className="rv__reject-box">
                  <textarea placeholder="Reason for rejection (optional)" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={3} />
                  <div className="rv__modal-actions">
                    <button className="rv__act" onClick={() => setShowReject(false)} disabled={busy}>Cancel</button>
                    <button className="rv__act rv__act--reject" onClick={handleReject} disabled={busy}>Confirm Reject</button>
                  </div>
                </div>
              )}

              {/* Delete — available for a review in any status, any time */}
              {!showReject && !deleteConfirm && (
                <div className="rv__modal-actions" style={{ marginTop: selected.status === 'pending' ? 8 : 0 }}>
                  <button
                    className="rv__act"
                    style={{ color: '#dc2626', borderColor: '#fca5a5' }}
                    disabled={busy}
                    onClick={() => setDeleteConfirm(true)}
                  >
                    Delete Review
                  </button>
                </div>
              )}
              {deleteConfirm && (
                <div className="rv__reject-box">
                  <p style={{ margin: '0 0 10px', fontSize: '0.85rem', color: '#7f1d1d' }}>
                    This moves the review to the Recycle Bin. If it's currently published on the trip page, it will be removed immediately.
                  </p>
                  <div className="rv__modal-actions">
                    <button className="rv__act" onClick={() => setDeleteConfirm(false)} disabled={busy}>Cancel</button>
                    <button className="rv__act rv__act--reject" onClick={handleDelete} disabled={busy}>
                      {busy ? 'Deleting…' : 'Confirm Delete'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
